import { Component, Input, forwardRef, ViewChild, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule, AbstractControl } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { FormErrorComponent } from '../form-error/form-error.component';
import { SelectOption } from '../select-field/select-field.component';

/**
 * IsMultiSelectFieldComponent
 *
 * Multi-select com chips, busca e dropdown moderno.
 * Valor emitido: array de values selecionados.
 *
 * @example
 * <is-multi-select-field
 *   label="Categorias"
 *   [options]="categories"
 *   placeholder="Selecione categorias..."
 *   formControlName="categories"
 * ></is-multi-select-field>
 */
@Component({
  selector: 'is-multi-select-field',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, FormErrorComponent],
  templateUrl: './multi-select-field.component.html',
  styleUrls: ['./multi-select-field.component.scss'],
  host: { '(document:mousedown)': 'onDocumentClick($event)' },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectFieldComponent),
      multi: true,
    },
  ],
})
export class MultiSelectFieldComponent implements ControlValueAccessor {
  @Input() label = '';
  @Input() placeholder = 'Selecione...';
  @Input() options: SelectOption[] = [];
  @Input() required = false;
  @Input() helpText = '';
  @Input() control: AbstractControl | null = null;
  @Input() maxItems = 0; // 0 = sem limite

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('multiSelectWrapper') multiSelectWrapper?: ElementRef<HTMLDivElement>;

  selectedValues: any[] = [];
  isDisabled = false;
  isOpen = false;
  searchTerm = '';
  filteredOptions: SelectOption[] = [];
  highlightedIndex = -1;

  private onChange: (val: any[]) => void = () => {};
  private onTouched: () => void = () => {};

  get hasError(): boolean {
    return !!(this.control?.invalid && (this.control?.dirty || this.control?.touched));
  }

  get selectedOptions(): SelectOption[] {
    return this.options.filter((o) => this.selectedValues.includes(o.value));
  }

  get isMaxReached(): boolean {
    return this.maxItems > 0 && this.selectedValues.length >= this.maxItems;
  }

  // ─── ControlValueAccessor ───────────────────────────────────────────────────

  writeValue(val: any): void {
    this.selectedValues = Array.isArray(val) ? [...val] : [];
  }

  registerOnChange(fn: (val: any[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.isDisabled = disabled;
  }

  // ─── Dropdown Control ───────────────────────────────────────────────────────

  toggle(): void {
    if (this.isDisabled) return;
    this.isOpen ? this.close() : this.open();
  }

  open(): void {
    this.isOpen = true;
    this.searchTerm = '';
    this.filterOptions();
    this.highlightedIndex = 0;
    setTimeout(() => this.searchInput?.nativeElement?.focus(), 50);
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.searchTerm = '';
    this.highlightedIndex = -1;
    this.onTouched();
  }

  // ─── Selection ──────────────────────────────────────────────────────────────

  toggleOption(opt: SelectOption): void {
    if (opt.disabled) return;

    const idx = this.selectedValues.indexOf(opt.value);
    if (idx >= 0) {
      this.selectedValues.splice(idx, 1);
    } else {
      if (this.isMaxReached) return;
      this.selectedValues.push(opt.value);
    }

    this.onChange([...this.selectedValues]);
  }

  removeChip(value: any, event: Event): void {
    event.stopPropagation();
    this.selectedValues = this.selectedValues.filter((v) => v !== value);
    this.onChange([...this.selectedValues]);
  }

  clearAll(event: Event): void {
    event.stopPropagation();
    this.selectedValues = [];
    this.onChange([]);
  }

  isSelected(value: any): boolean {
    return this.selectedValues.includes(value);
  }

  filterOptions(): void {
    const term = this.searchTerm.toLowerCase();
    this.filteredOptions = this.options.filter((o) =>
      o.label.toLowerCase().includes(term)
    );
    this.highlightedIndex = 0;
  }

  // ─── Click Outside ────────────────────────────────────────────────────────

  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) return;
    const target = event.target as HTMLElement;
    if (!this.multiSelectWrapper?.nativeElement?.contains(target)) {
      this.close();
    }
  }

  // ─── Keyboard ───────────────────────────────────────────────────────────────

  onKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.toggle();
        break;
      case 'Escape':
        this.close();
        break;
    }
  }

  onSearchKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Enter':
        event.preventDefault();
        if (this.highlightedIndex >= 0 && this.filteredOptions[this.highlightedIndex]) {
          this.toggleOption(this.filteredOptions[this.highlightedIndex]);
        }
        break;
      case 'Escape':
        this.close();
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.filteredOptions.length - 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
        break;
    }
  }
}
